import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function Contact() {
  useEffect(() => {
    document.title = "Contact Us | Tablecure";
  }, []);

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState(null); // null | "sending" | "sent" | "error"

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/tablecure", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus("sent");
        setForm({ name: "", email: "", subject: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="policy-page tc-page">
      <div className="pd2-bg">
        <div className="policy-orb policy-orb1" />
        <div className="policy-orb policy-orb2" />
        <div className="ck-grid" />
      </div>

      <div className="policy-inner contact-inner">
        <div className="policy-header">
          <div className="pd2-eyebrow">
            <span className="pd2-eyebrow-dot" />
            Support
          </div>
          <h1 className="policy-title">Contact Us</h1>
          <p className="policy-subtitle">
            We're here to help. Reach out and we'll get back to you within 2 business days.
          </p>
        </div>

        <div className="contact-grid">
          {/* Contact channels */}
          <div className="contact-channels">
            <h2 className="contact-channels-title">Get in touch</h2>

            <div className="contact-channel">
              <div className="contact-channel-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div>
                <div className="contact-channel-label">Email</div>
                <a className="contact-channel-value" href="mailto:support@tablecure.com">
                  support@tablecure.com
                </a>
              </div>
            </div>

            <div className="contact-channel">
              <div className="contact-channel-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div>
                <div className="contact-channel-label">Response Time</div>
                <div className="contact-channel-value">Within 2 business days</div>
              </div>
            </div>

            <div className="contact-channel">
              <div className="contact-channel-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div>
                <div className="contact-channel-label">Warranty &amp; Support</div>
                <div className="contact-channel-value">2-year warranty on all products</div>
              </div>
            </div>

            <div className="contact-faq">
              <div className="contact-faq-title">Common questions</div>
              <div className="contact-faq-item">
                <div className="contact-faq-q">How do I track my order?</div>
                <div className="contact-faq-a">
                  Log into <Link to="/dashboard">My Account</Link> to see live order status,
                  or check the tracking email we sent after dispatch.
                </div>
              </div>
              <div className="contact-faq-item">
                <div className="contact-faq-q">Can I return my order?</div>
                <div className="contact-faq-a">
                  Yes — within 7 days of delivery. See our{" "}
                  <Link to="/shipping-policy">Shipping &amp; Returns Policy</Link> for details.
                </div>
              </div>
              <div className="contact-faq-item">
                <div className="contact-faq-q">What is the warranty period?</div>
                <div className="contact-faq-a">
                  All Tablecure products carry a 2-year manufacturer's warranty against defects.
                </div>
              </div>
              <div className="contact-faq-item">
                <div className="contact-faq-q">Do you ship internationally?</div>
                <div className="contact-faq-a">
                  Yes, we offer free worldwide shipping. See{" "}
                  <Link to="/shipping-policy">Shipping &amp; Returns</Link> for timelines.
                </div>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="contact-form-wrap">
            <h2 className="contact-form-title">Send us a message</h2>

            {status === "sent" ? (
              <div className="contact-success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <p><strong>Message sent!</strong> We'll get back to you within 2 business days.</p>
                <button className="pd2-btn-ghost" onClick={() => setStatus(null)}>
                  Send another message
                </button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-form-row">
                  <div className="contact-field">
                    <label htmlFor="cf-name">Full Name</label>
                    <input
                      id="cf-name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      minLength={2}
                    />
                  </div>
                  <div className="contact-field">
                    <label htmlFor="cf-email">Email Address</label>
                    <input
                      id="cf-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>
                <div className="contact-field">
                  <label htmlFor="cf-subject">Subject</label>
                  <select
                    id="cf-subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a topic…</option>
                    <option value="Order / Shipping">Order / Shipping</option>
                    <option value="Return / Refund">Return / Refund</option>
                    <option value="Warranty Claim">Warranty Claim</option>
                    <option value="Product Question">Product Question</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="contact-field">
                  <label htmlFor="cf-message">Message</label>
                  <textarea
                    id="cf-message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help…"
                    required
                    minLength={20}
                    rows={5}
                  />
                </div>

                {status === "error" && (
                  <p className="contact-error">
                    Something went wrong. Please email us directly at{" "}
                    <a href="mailto:support@tablecure.com">support@tablecure.com</a>.
                  </p>
                )}

                <button
                  type="submit"
                  className="pd2-btn-primary"
                  disabled={status === "sending"}
                >
                  {status === "sending" ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="policy-footer-nav">
          <Link to="/" className="pd2-btn-ghost">← Back to Home</Link>
          <div className="policy-footer-links">
            <Link to="/privacy-policy" className="policy-footer-link">Privacy Policy</Link>
            <span className="policy-footer-sep" />
            <Link to="/terms" className="policy-footer-link">Terms</Link>
            <span className="policy-footer-sep" />
            <Link to="/shipping-policy" className="policy-footer-link">Shipping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
